import { useState, useCallback } from "react";
import * as Scrivito from "scrivito";
import ClipLoader from "react-spinners/ClipLoader";
import Modal from "react-modal";
import { isBrowser } from "^/utils/isBrowser";
import FormPageWidget from "./FormPageWidget/FormPageWidgetComponent";

function fieldType(widgetClass) {
  const mapping = {
    InputWidget: "text",
    CheckboxWidget: "checkbox",
    SelectboxWidget: "select",
    ReceiverEmailInputWidget: "email",
    TextAreaWidget: "textarea",
    RadioboxWidget: "radio",
  };
  return mapping[widgetClass] || "text";
}

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    background: "none",
    border: 0,
  },
};

function isInputField(widgetClass) {
  const avaliableInputClasses = [
    "CheckboxWidget",
    "InputWidget",
    "RadioboxWidget",
    "ReceiverEmailInputWidget",
    "SelectboxWidget",
    "TextAreaWidget",
  ];
  return avaliableInputClasses.indexOf(widgetClass) !== -1;
}

function readValue(inputId, objClass) {
  const sessionStorageObject = isBrowser ? window.sessionStorage : {};
  return objClass === "CheckboxWidget"
    ? JSON.parse(sessionStorageObject[inputId] || "[]")
    : sessionStorageObject[inputId] || "";
}

function readPossibleValues(inputWidget, objClass) {
  const mapping = {
    RadioboxWidget: "radioButtons",
    SelectboxWidget: "options",
    CheckboxWidget: "checkboxOptions",
  };
  if (Object.keys(mapping).indexOf(objClass) === -1) {
    return null;
  }
  return inputWidget
    .get(mapping[objClass])
    .map((item) => ({ text: item.get("text") }));
}

function FormWidget({ widget }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const goNextStep = useCallback(() => setStep((s) => s + 1), []);
  const goPrevStep = useCallback(() => setStep((s) => s - 1), []);

  const submitAnswers = useCallback(() => {
    const objId = widget.obj().id();
    setSubmitting(true);
    const csvColumns = [];
    const inputs = widget
      .widgets()
      .filter((subWidget) => isInputField(subWidget.objClass()));

    const answers = [];

    inputs.forEach((input, index) => {
      const inputId = input.id();
      const objClass = input.objClass();
      const colName = input.get("csvColumnName");
      csvColumns.push(colName);
      answers.push({
        index,
        label: input.get("label"),
        csvColumnName: colName,
        value: readValue(inputId, objClass),
        type: fieldType(objClass),
        possibleValues: readPossibleValues(input, objClass),
        receiverEmail: objClass === "ReceiverEmailInputWidget",
      });
    });

    const formObject = {
      data: {
        type: "contact_request",
        attributes: {
          objId,
          tenantId: process.env.SCRIVITO_TENANT,
          fields: answers,
          emailTitle: widget.get("emailTitle"),
          confirmationEmailText: widget.get("confirmationEmailText"),
          emailCSVReceiver: widget.get("emailCSVReceiver"),
          csvColumns,
        },
      },
    };
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formObject),
    };
    fetch(process.env.FORM_SERVICE, requestOptions)
      .then((response) => {
        if (!response.ok) {
          setSubmitting(false);
        } else {
          setSubmitting(false);
          goNextStep();
          window.sessionStorage.clear();
        }
      })
      .catch(() => {
        setSubmitting(false);
      });
  }, [widget, goNextStep]);

  const formPages = widget.get("formPages");

  return (
    <>
      {step === 0 && (
        <Scrivito.ContentTag
          content={widget}
          attribute="introPage"
          widgetProps={{
            nextStep: goNextStep,
          }}
        />
      )}
      {step > formPages.length && (
        <Scrivito.ContentTag content={widget} attribute="outroPage" />
      )}
      {step > 0 && step <= formPages.length && (
        <FormPageWidget
          widget={formPages[step - 1]}
          goNextStep={goNextStep}
          goPrevStep={goPrevStep}
          submitAnswers={submitAnswers}
          currentStep={step}
          formPagesCount={formPages.length}
        />
      )}
      {typeof document !== "undefined" && (
        <Modal
          isOpen={submitting}
          style={customStyles}
          appElement={document.getElementById("application")}
        >
          <div className="d-flex justify-content-center align-items-center">
            <ClipLoader color={"#354E91"} loading={submitting} size={120} />
          </div>
        </Modal>
      )}
    </>
  );
}

Scrivito.provideComponent("FormWidget", FormWidget);
